import { useLazyQuery, useQuery } from '@apollo/client';
import GetAppIcon from '@mui/icons-material/GetApp';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { openDownloadUrl } from '../../../components/Downloads/helpers';
import {
  GET_AVAILABLE_DOWNLOADS_QUERY,
  SECURE_COUNTRY_DOWNLOAD_QUERY,
} from '../../../components/Downloads/operations';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { getUserId } from '../../../utils/auth';
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENTS } from '../../../utils/constants';
import { REPORT_TYPES } from '../../Valuations/constants';
import { SECURE_REPORT_DOWNLOAD_QUERY } from './operations';

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    ...theme.palette.mapInfoSection,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '.5em',
    marginBottom: '1em',
  },
  label: {
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'center',
  },
  orangeIcon: {
    ...theme.palette.accentText,
  },
}));

export default ({ displayedItem }) => {
  const classes = useStyles();
  const [reportType, setReportType] = useState('');
  const [skipQuery, setSkipQuery] = useState(true);
  const { setSnack } = useContext(SnackbarContext);
  const { legacyId: assetLegacyId, countryIsoCode } = displayedItem;
  const showValuationAndAssetReports = displayedItem.entityType === 'asset';
  const dimensions = displayedItem
    ? { asset: displayedItem.name, country: displayedItem.countryName, company: 'NULL' }
    : {};

  const getReportDownloadLink = reportType => {
    const gaReportType =
      reportType === REPORT_TYPES.VALUATIONS_MODEL ? 'valuation_model' : 'asset_report';
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: 'Maps',
      event_action: ANALYTICS_EVENT_ACTIONS.download,
      event_label: gaReportType,
      userId: getUserId(),
      ...dimensions,
    });
    setReportType(reportType);
    setSkipQuery(false);
  };

  const { loading: download_loading, data: download_data } = useQuery(
    SECURE_REPORT_DOWNLOAD_QUERY,
    {
      variables: { assetLegacyId, isoCode: countryIsoCode, reportType },
      fetchPolicy: 'network-only',
      skip: skipQuery,
      onCompleted: data => {
        const { url, downloadMessage } = data.secureReportDownload;

        if (url) {
          setSnack({
            open: true,
            message: downloadMessage,
            severity: 'success',
          });
        }
      },
      onError: error => {
        setSnack({
          severity: 'error',
          message: error.message,
          open: true,
        });
      },
    },
  );

  useEffect(() => {
    if (download_loading || !download_data) return null;
    const { url, filename } = download_data.secureReportDownload;
    if (url && filename) {
      openDownloadUrl(url, filename);
    }
    setSkipQuery(true);
    setReportType('');
  }, [download_loading, download_data]);

  const { loading, data } = useQuery(GET_AVAILABLE_DOWNLOADS_QUERY, {
    variables: {
      countryIsoCode,
    },
    skip: !countryIsoCode,
  });

  const hasCoreReport = useMemo(() => {
    return !loading && data?.availableCountryDownloads?.titles?.includes('COUNTRY OVERVIEW REPORT');
  }, [loading, data?.availableCountryDownloads?.titles]);

  const hasGISData = useMemo(() => {
    return !loading && data?.availableCountryDownloads?.titles?.includes('GIS DATA');
  }, [loading, data?.availableCountryDownloads?.titles]);

  const [getSecureCountryDownload] = useLazyQuery(SECURE_COUNTRY_DOWNLOAD_QUERY, {
    fetchPolicy: 'network-only',
    variables: { downloadType: 'COUNTRY OVERVIEW REPORT', countryIsoCode },
    onCompleted: data => {
      const { url, downloadMessage } = data.secureCountryDownload;
      setSnack({
        open: true,
        message: `Your download should begin shortly.${downloadMessage}`,
        severity: 'success',
      });
      window.location.href = url;
    },
    onError: error => {
      setSnack({
        severity: 'error',
        message: error.message,
        open: true,
      });
    },
  });

  const getDownloadLink = useCallback(() => {
    if (countryIsoCode === null) return;
    getSecureCountryDownload({
      variables: { downloadType: 'GIS DATA', countryIsoCode: countryIsoCode },
    });
  }, [countryIsoCode, getSecureCountryDownload]);

  return (
    <div className={classes.buttonContainer}>
      {!loading && showValuationAndAssetReports ? (
        <>
          <Button
            onClick={() => {
              getReportDownloadLink(REPORT_TYPES.VALUATIONS_MODEL);
            }}
            variant='contained'
            color='secondary'
          >
            <GetAppIcon fontSize='small' />
            <div className={classes.label}>Valuation model</div>
          </Button>
          <Button
            onClick={() => {
              getReportDownloadLink(REPORT_TYPES.ASSET_REPORT);
            }}
            variant='contained'
            color='secondary'
          >
            <GetAppIcon fontSize='small' />
            <div className={classes.label}>Asset report</div>
          </Button>
        </>
      ) : null}
      {hasCoreReport ? (
        <Button onClick={() => getSecureCountryDownload()} variant='contained' color='secondary'>
          <GetAppIcon fontSize='small' />
          <div className={classes.label}>Country overview</div>
        </Button>
      ) : null}
      {hasGISData ? (
        <Button onClick={getDownloadLink} variant='contained' color='secondary'>
          <GetAppIcon fontSize='small' />
          <div className={classes.label}>GIS data</div>
        </Button>
      ) : null}
    </div>
  );
};
